import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Award, TrendingDown, Scale, SlidersHorizontal, ClipboardCheck, Gavel } from "lucide-react";
import { Card } from "@/components/ui/card";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ConferenceLog, { hasConference } from "@/components/ConferenceLog";
import PageHeader, { fmtDate } from "@/components/PageHeader";
import { useFeatureFlags } from "@/lib/featureFlags";
import { useAuth } from "@/context/AuthContext";
import SubmissionReviewCard, { ReviewActions, EMPTY_TEXT, groupCounts, inGroup, useReveal, STICKY_CELL } from "@/components/review/SubmissionReviewCard";
import { MeritCaseRow, caseColumns, CASE_FILTERS, CASE_GROUPS, CASE_EMPTY } from "@/components/merits/MeritCaseCells";
import { shortNo } from "@/lib/caseNo";
import ReportExportDialog from "@/components/ReportExportDialog";

const MERIT_REPORTS = [
  { key: "requests", label: "Merit Requests", endpoint: "/coach/exports/merit-requests", dateLabel: "Event",
    statuses: [["pending", "Pending"], ["approved", "Approved"], ["declined", "Declined"]],
    description: "Merit requests submitted by team members, with event, description, awarded value and status.", hint: "Leave dates empty to include every request." },
  { key: "cases", label: "Merit Cases", endpoint: "/coach/exports/merit-cases", dateLabel: "Issued",
    statuses: CASE_FILTERS.filter(([k]) => k !== "all"),
    description: "Merit and demerit cases with type, value, description and status.", hint: "Leave dates empty to include every case." },
];

const VALUES = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];

export function VerdictDialog({ target, onCancel, title, description, confirmText, destructive, onConfirm }) {
  const [notes, setNotes] = useState("");
  useEffect(() => { setNotes(""); }, [target]);
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="verdict-dialog">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{description}</p>
        <div className="space-y-1.5">
          <Label className="text-xs">Conference notes (optional)</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes for the conference log" data-testid="verdict-notes-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onCancel} data-testid="verdict-cancel-btn">Cancel</Button>
          <Button className={`rounded-full ${destructive ? "bg-red-600 hover:bg-red-700" : "bg-slate-900 hover:bg-slate-800"}`} onClick={() => onConfirm(notes)} data-testid="verdict-confirm-btn">{confirmText}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MeritDialog({ open, setOpen, members, editing, onDone }) {
  const { schedule: scheduleOn } = useFeatureFlags();
  const [form, setForm] = useState({ team_member_id: "", case_type: "merit", value: "1", magnitude: "1", preset_id: "", description: "", conference_date: "", conference_time: "" });
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) api.get("/coach/merit-presets").then(({ data }) => setPresets(data.items || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (editing) setForm({ team_member_id: editing.team_member_id, case_type: editing.case_type, value: String(editing.value), magnitude: String(Math.abs(editing.value)), preset_id: editing.preset_id || "", description: editing.description, conference_date: (editing.conference_date || "").slice(0, 10), conference_time: editing.conference_time || "" });
    else setForm({ team_member_id: "", case_type: "merit", value: "1", magnitude: "1", preset_id: "", description: "", conference_date: "", conference_time: "" });
  }, [editing, open]);

  const selectedPreset = presets.find((p) => p.id === form.preset_id) || null;

  const onPresetChange = (v) => {
    if (v === "other") { setForm((f) => ({ ...f, preset_id: "" })); return; }
    const p = presets.find((x) => x.id === v);
    if (!p) return;
    setForm((f) => ({ ...f, preset_id: v, case_type: p.category, magnitude: p.value_type === "single" ? String(p.value) : String(p.min) }));
  };

  const computeValue = () => {
    if (selectedPreset) {
      const mag = parseInt(form.magnitude, 10);
      if (isNaN(mag)) return { err: "Enter a value" };
      if (selectedPreset.value_type === "range" && (mag < selectedPreset.min || mag > selectedPreset.max)) {
        return { err: `Value must be between ${selectedPreset.min} and ${selectedPreset.max}` };
      }
      if (selectedPreset.value_type === "single" && mag !== selectedPreset.value) {
        return { err: `This policy requires a value of ${selectedPreset.value}` };
      }
      return { value: selectedPreset.category === "merit" ? mag : -mag, case_type: selectedPreset.category };
    }
    return { value: parseInt(form.value, 10), case_type: form.case_type };
  };

  const submit = async () => {
    const { value, case_type, err } = computeValue();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      if (editing) {
        const payload = { case_type, value, description: form.description, preset_id: form.preset_id || null };
        if (form.conference_date) { payload.conference_date = form.conference_date; payload.conference_time = form.conference_time || ""; }
        await api.put(`/coach/merits/${editing.id}`, payload);
        toast.success("Case updated");
      } else {
        if (!form.team_member_id) { toast.error("Select a team member"); setLoading(false); return; }
        await api.post("/coach/merits", { team_member_id: form.team_member_id, case_type, value, description: form.description, preset_id: form.preset_id || null });
        toast.success("Case created");
      }
      setOpen(false); onDone();
    } catch (err2) { toast.error(formatApiError(err2.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="merit-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${shortNo(editing.case_number)}` : "New merit / demerit case"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!editing && (
            <div className="space-y-2">
              <Label>Team member</Label>
              <Select value={form.team_member_id} onValueChange={(v) => setForm((f) => ({ ...f, team_member_id: v }))}>
                <SelectTrigger data-testid="merit-member-select"><SelectValue placeholder="Select member" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Merit policy</Label>
            <Select value={form.preset_id || "other"} onValueChange={onPresetChange}>
              <SelectTrigger data-testid="merit-preset-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {presets.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`merit-preset-${p.id}`}>
                    {p.category === "merit" ? "Merit" : "Demerit"} · {p.name} ({p.value_type === "range" ? `${p.min}–${p.max}` : p.value})
                  </SelectItem>
                ))}
                <SelectItem value="other" data-testid="merit-preset-other">Other (manual entry)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.case_type} onValueChange={(v) => setForm((f) => ({ ...f, case_type: v }))} disabled={!!selectedPreset}>
                <SelectTrigger data-testid="merit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="merit">Merit</SelectItem><SelectItem value="demerit">Demerit</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Value{selectedPreset?.value_type === "range" && <span className="ml-1 text-xs text-slate-400">({selectedPreset.min}–{selectedPreset.max})</span>}</Label>
              {!selectedPreset ? (
                <Select value={form.value} onValueChange={(v) => setForm((f) => ({ ...f, value: v }))}>
                  <SelectTrigger data-testid="merit-value-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{VALUES.map((v) => <SelectItem key={v} value={String(v)}>{v > 0 ? `+${v}` : v}</SelectItem>)}</SelectContent>
                </Select>
              ) : selectedPreset.value_type === "single" ? (
                <Input readOnly value={`${selectedPreset.category === "merit" ? "+" : "-"}${selectedPreset.value}`} className="bg-slate-50 font-semibold" data-testid="merit-value-fixed" />
              ) : (
                <Input type="number" min={selectedPreset.min} max={selectedPreset.max} value={form.magnitude}
                  onChange={(e) => setForm((f) => ({ ...f, magnitude: e.target.value }))} data-testid="merit-value-range-input" />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="merit-description-input" />
          </div>
          {scheduleOn && editing && editing.acknowledgement_submitted && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Conference date {editing.acknowledgement_choice === "conference_requested" && <span className="text-sky-600">(requested)</span>}</Label>
                <Input type="date" value={form.conference_date} onChange={(e) => setForm((f) => ({ ...f, conference_date: e.target.value }))} data-testid="merit-conference-input" />
              </div>
              <div className="space-y-2">
                <Label>Time <span className="text-slate-400">(optional)</span></Label>
                <Input type="time" value={form.conference_time} disabled={!form.conference_date} onChange={(e) => setForm((f) => ({ ...f, conference_time: e.target.value }))} data-testid="merit-conference-time-input" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="merit-submit-btn" className="rounded-full bg-slate-900 hover:bg-slate-800">{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveDialog({ request, open, setOpen, onDone }) {
  const isSingle = request?.value_type === "single";
  const mn = request?.preset_min ?? 1;
  const mx = request?.preset_max ?? 10;
  const [value, setValue] = useState(String(mn));
  const [loading, setLoading] = useState(false);
  useEffect(() => { setValue(String(mn)); }, [open, mn]);

  const rangeOptions = [];
  for (let i = mn; i <= mx; i += 1) rangeOptions.push(i);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = isSingle ? {} : { value: parseInt(value, 10) };
      await api.post(`/coach/merit-requests/${request.id}/approve`, payload);
      toast.success("Request approved");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="approve-request-dialog">
        <DialogHeader><DialogTitle className="font-heading">Approve {shortNo(request?.request_number)}</DialogTitle>
          <DialogDescription>Review the request and award the merit points.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-500">{request?.event_name} — {request?.event_description}</p>
        <div className="space-y-2">
          <Label>Merit value to award</Label>
          {isSingle ? (
            <>
              <Input readOnly value={`+${request?.assigned_value}`} className="bg-slate-50 font-semibold" data-testid="approve-value-fixed" />
              <p className="text-xs text-slate-400" data-testid="approve-fixed-hint">This merit event has a fixed point value set by the policy — it can't be changed.</p>
            </>
          ) : (
            <>
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger data-testid="approve-value-select"><SelectValue /></SelectTrigger>
                <SelectContent>{rangeOptions.map((v) => <SelectItem key={v} value={String(v)}>+{v}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-400" data-testid="approve-range-hint">This event allows {mn}–{mx} points. Choose the value to award.</p>
            </>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="approve-submit-btn" className="rounded-full bg-emerald-600 hover:bg-emerald-700">Approve &amp; award</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ request, open, setOpen, onDone }) {
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { setComment(""); }, [open]);

  const submit = async () => {
    if (!comment.trim()) { toast.error("Please write a reason for returning"); return; }
    setLoading(true);
    try {
      await api.post(`/coach/merit-requests/${request.id}/return`, { comment });
      toast.success("Returned for correction");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="return-request-dialog">
        <DialogHeader><DialogTitle className="font-heading">Return for correction — {shortNo(request?.request_number)}</DialogTitle>
          <DialogDescription>Tell the team member what to fix so they can resubmit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Reason (required)</Label>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="return-comment-input" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} data-testid="return-submit-btn" className="rounded-full bg-violet-600 hover:bg-violet-700">Return</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const REQUEST_GROUPS = { pending: ["pending", "returned_for_correction"], approved: ["approved"], declined: ["disapproved"] };
const REQUEST_COLUMNS = ["Request #", "Member", "Event", "Event Date", "Description", "Status", "Actions"];

function MeritRequestRow({ r, viewOnly, onApprove, onDecline, onReturn }) {
  const open = REQUEST_GROUPS.pending.includes(r.status);
  return (
    <TableRow data-testid={`coach-request-row-${r.request_number}`}>
      <TableCell className="font-mono text-xs font-semibold">{shortNo(r.request_number)}</TableCell>
      <TableCell className={`whitespace-nowrap text-sm font-medium ${STICKY_CELL}`}>{r.team_member_name}</TableCell>
      <TableCell className="text-sm font-medium text-slate-800">{r.event_name}</TableCell>
      <TableCell className="text-sm">{fmtDate(r.event_date)}</TableCell>
      <TableCell className="max-w-xs text-sm text-slate-600">{r.event_description}</TableCell>
      <TableCell className="max-w-[12rem]">
        <StatusBadge status={r.status} />
        {r.status === "approved" && r.assigned_value && <span className="ml-1 text-xs font-bold text-emerald-600">+{r.assigned_value}</span>}
        {r.status === "returned_for_correction" && r.returned_comment && <p className="mt-1 text-xs text-violet-700">{r.returned_comment}</p>}
      </TableCell>
      <TableCell>
        {open && !viewOnly ? (
          <ReviewActions onApprove={() => onApprove(r)} onDecline={() => onDecline(r)} onReturn={() => onReturn(r)}
            testIds={{ approve: `approve-request-${r.request_number}`, decline: `disapprove-request-${r.request_number}`, return: `return-request-${r.request_number}` }} />
        ) : <span className="text-xs text-slate-400">—</span>}
      </TableCell>
    </TableRow>
  );
}

function MeritRequestsSection({ requests, onDone: reload }) {
  const { viewOnly } = useAuth();
  const onDone = () => { reload(); window.dispatchEvent(new Event("rpg:badges-refresh")); };
  const [approve, setApprove] = useState(null);
  const [ret, setRet] = useState(null);
  const [filter, setFilter] = useState("pending");
  const counts = useMemo(() => groupCounts(REQUEST_GROUPS, requests), [requests]);
  const filtered = useMemo(() => requests.filter((r) => inGroup(REQUEST_GROUPS, filter, r.status)), [requests, filter]);
  const { visible, hasMore, loadMore } = useReveal(filtered);

  const disapprove = async (r) => {
    try { await api.post(`/coach/merit-requests/${r.id}/disapprove`); toast.success("Request declined"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <SubmissionReviewCard title="Merit Requests" icon={ClipboardCheck} iconClass="bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"
        filter={filter} onFilter={setFilter} counts={counts} columns={REQUEST_COLUMNS} rowCount={visible.length}
        emptyText={EMPTY_TEXT[filter]} hasMore={hasMore} onLoadMore={loadMore} testId="coach-merit-requests">
        {visible.map((r) => <MeritRequestRow key={r.id} r={r} viewOnly={viewOnly} onApprove={setApprove} onDecline={disapprove} onReturn={setRet} />)}
      </SubmissionReviewCard>
      {approve && <ApproveDialog request={approve} open={!!approve} setOpen={(o) => !o && setApprove(null)} onDone={onDone} />}
      {ret && <ReturnDialog request={ret} open={!!ret} setOpen={(o) => !o && setRet(null)} onDone={onDone} />}
    </>
  );
}

export default function CoachMerits() {
  const navigate = useNavigate();
  const { plan } = useFeatureFlags();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [params] = useSearchParams();
  const linkedFilter = params.get("filter");
  const [caseFilter, setCaseFilter] = useState(CASE_FILTERS.some(([k]) => k === linkedFilter) ? linkedFilter : "open");

  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/coach/merits"), api.get("/coach/team-members"), api.get("/coach/merit-requests")]);
    setItems(a.data.items); setMembers(b.data.items); setRequests(c.data.items);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (linkedFilter && items.length) document.getElementById("merit-cases")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [linkedFilter, items.length]);
  const teamMerits = members.reduce((s, m) => s + (m.total_merits || 0), 0);
  const teamDemerits = members.reduce((s, m) => s + (m.total_demerits || 0), 0);
  const teamNet = teamMerits - teamDemerits;
  const [rescindTarget, setRescindTarget] = useState(null);
  const [upholdTarget, setUpholdTarget] = useState(null);

  const doRescind = async (notes) => {
    const t = rescindTarget;
    setRescindTarget(null);
    if (!t) return;
    try { await api.post(`/coach/merits/${t.id}/rescind`, { notes: notes || null }); toast.success("Case repealed & points removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doUphold = async (notes) => {
    const t = upholdTarget;
    setUpholdTarget(null);
    if (!t) return;
    try { await api.post(`/coach/merits/${t.id}/uphold`, { notes: notes || null }); toast.success("Case upheld — points now count"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };


  // Cases: member last name A–Z (team_member_name is "Last, First"), newest first within a member.
  const caseCounts = useMemo(() => groupCounts(CASE_GROUPS, items, CASE_FILTERS), [items]);
  const filteredCases = useMemo(() => items
    .filter((m) => inGroup(CASE_GROUPS, caseFilter, m.status))
    .sort((a, b) => (a.team_member_name || "").toLowerCase().localeCompare((b.team_member_name || "").toLowerCase())
      || (b.date_issued || "").localeCompare(a.date_issued || "")), [items, caseFilter]);
  const { visible: caseRows, hasMore: moreCases, loadMore: loadMoreCases } = useReveal(filteredCases);

  return (
    <div>
      <PageHeader title="Merit System" subtitle="Merit and demerit cases for your team"
        actions={<div className="flex flex-wrap gap-2">
          <ReportExportDialog title="Export merit system" reports={MERIT_REPORTS} accent="emerald" testId="merit-export"
            members={members.map((m) => ({ id: m.id, name: `${m.first_name} ${m.last_name}` }))} />
          {plan !== "base" && <Button variant="outline" className="rounded-full" onClick={() => navigate("/coach/merit-options")} data-testid="coach-merit-options-btn"><SlidersHorizontal className="mr-2 h-4 w-4" /> Settings</Button>}
        </div>} />

      <Card className="mb-6 flex items-stretch divide-x divide-slate-200 border-slate-200 shadow-sm" data-testid="coach-merits-summary-bar">
        <div className="flex flex-1 items-center gap-2.5 px-3 py-3 sm:px-5" data-testid="coach-merits-total-merits">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600"><Award className="h-4 w-4" /></span>
          <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">Team Merits</p><p className="text-lg font-extrabold tabular-nums text-emerald-600 sm:text-xl">{teamMerits}</p></div>
        </div>
        <div className="flex flex-1 items-center gap-2.5 px-3 py-3 sm:px-5" data-testid="coach-merits-total-demerits">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600"><TrendingDown className="h-4 w-4" /></span>
          <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">Team Demerits</p><p className="text-lg font-extrabold tabular-nums text-red-600 sm:text-xl">{teamDemerits}</p></div>
        </div>
        <div className="flex flex-1 items-center gap-2.5 px-3 py-3 sm:px-5" data-testid="coach-merits-net-score">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-600"><Scale className="h-4 w-4" /></span>
          <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">Team Net</p><p className={`text-lg font-extrabold tabular-nums sm:text-xl ${teamNet < 0 ? "text-red-600" : "text-sky-600"}`}>{teamNet}</p></div>
        </div>
      </Card>

      <MeritRequestsSection requests={requests} onDone={load} />

      <div className="mb-6 flex justify-end">
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-merit-btn"><Plus className="mr-2 h-4 w-4" /> New case</Button>
      </div>

      <SubmissionReviewCard id="merit-cases" title="Merit & Demerit Cases" icon={Gavel} iconClass="bg-red-100 text-red-700 ring-1 ring-red-200"
        filters={CASE_FILTERS} filter={caseFilter} onFilter={setCaseFilter} counts={caseCounts} columns={caseColumns(true).concat("")}
        rowCount={caseRows.length} emptyText={CASE_EMPTY[caseFilter]} hasMore={moreCases} onLoadMore={loadMoreCases} testId="coach-merits">
        {caseRows.map((m) => (
          <MeritCaseRow key={m.id} c={m} withMember confTestId="conference-scheduled" testId={`coach-merit-row-${m.case_number}`}
            buttons={m.status === "rescinded" ? (
                hasConference(m) ? <ConferenceLog caseItem={m} /> : null
              ) : (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditing(m); setOpen(true); }} data-testid={`edit-merit-${m.case_number}`}>Edit</Button>
                  {(m.status === "conference_requested" || m.status === "conference_date") && (
                    <Button size="sm" variant="outline" className="rounded-full text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => setUpholdTarget(m)} data-testid={`uphold-merit-${m.case_number}`}>Uphold</Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRescindTarget(m)} data-testid={`rescind-merit-${m.case_number}`}>Rescind</Button>
                  {hasConference(m) && <ConferenceLog caseItem={m} />}
                </div>
              )} />
        ))}
      </SubmissionReviewCard>
      <MeritDialog open={open} setOpen={setOpen} members={members} editing={editing} onDone={load} />
      <VerdictDialog
        target={rescindTarget}
        onCancel={() => setRescindTarget(null)}
        title={`Rescind case ${shortNo(rescindTarget?.case_number)}?`}
        description="This repeals the case, removes its points from the member's score, and notifies the member. This cannot be undone."
        confirmText="Rescind case"
        destructive
        onConfirm={doRescind}
      />
      <VerdictDialog
        target={upholdTarget}
        onCancel={() => setUpholdTarget(null)}
        title={`Uphold case ${shortNo(upholdTarget?.case_number)}?`}
        description="This closes the conference in the coach's favor: the case is finalized as No Further Action, its points start counting, and the member is notified."
        confirmText="Uphold case"
        onConfirm={doUphold}
      />
    </div>
  );
}
